import {
  jsonb,
  text,
  timestamp,
  varchar,
  boolean,
  integer,
  primaryKey,
} from "drizzle-orm/pg-core"
import { email_length, id_length, ulidId } from "./shared"

import { coreExportSchema } from "./core_products"
import { templateRevision } from "./template"
import {
  exportRequest,
  transferRequest,
  supportingDocument,
  partyRegistration,
  supportingDocumentFile,
} from "./schema"

/**
 * All historic data tables should be populated by triggers.
 */

export const transferRequestHistory = coreExportSchema.table(
  "transfer_request_history",
  {
    id: ulidId(), // format should be tdh-<ulid>
    transferRequestId: varchar("transfer_request_id", {
      length: id_length,
    })
      .notNull()
      .references(() => transferRequest.id, { onDelete: "cascade" }),
    status: text("status", {
      enum: [
        "DRAFT",
        "RAISED",
        "CANCELLED",
        "RESUBMIT",
        "AMENDED",
        "APPROVED",
        "REVOKED",
        "ACKNOWLEDGED",
        "ACCEPTED",
        "REQUEST_REPLACEMENT",
        "PENDING_REPLACEMENT",
        "REVOKED",
        "REJECTED",
        "PARTIAL",
        "DETAINED",
        "REPLACED",
        "REPLACEMENT_AUTHORISED",
      ],
    }).notNull(),
    substatus: text("substatus"),
    reasonForStatusChange: text("reason_for_state_change"),
    action: text("action", {
      enum: [
        "RAISE",
        "AMEND",
        "STATUS_CHANGE",
        "TRANSPORT_DETAILS_UPDATED",
        "EXHAUSTION_UPDATED",
        "FULLY_EXHAUSTED",
        "FULLY_UN_EXHAUSTED",
        "RESTORE",
        "TRANSFERRED",
        "RESUBMIT",
        "TRACES",
        "TRANSFER",
        "AUDIT_COMMENT",
      ],
    }).notNull(),
    autoApproved: boolean("auto_approved"),
    reasonCategories: jsonb("reason_categories"), // Array of reasons selected by user
    templateVersionId: varchar("template_version_id", {
      length: id_length,
    }).references(() => templateRevision.id, { onDelete: "cascade" }),
    nzxmlURI: text("nzxml_uri"), // URI from NZXML system, if applicable
    snapshotURI: text("snapshot_uri"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by", { length: email_length }), // ID for user
    updatedByProfileName: varchar("updated_by_profile", { length: 20 }), // B2G Profile Name used by user

    updatedBySystem: varchar("updated_by_system", { length: id_length }), // ID for user
  },
)

export const exportRequestHistory = coreExportSchema.table(
  "export_request_history",
  {
    id: ulidId(), // format should be erh-<ulid>
    exportRequestId: varchar("export_request_id", {
      length: id_length,
    })
      .notNull()
      .references(() => exportRequest.id, { onDelete: "cascade" }),
    status: text("status", {
      enum: [
        "DRAFT",
        "RAISED",
        "CANCELLED",
        "RESUBMIT",
        "AMENDED",
        "APPROVED",
        "REVOKED",
        "ACKNOWLEDGED",
        "ACCEPTED",
        "REQUEST_REPLACEMENT",
        "PENDING_REPLACEMENT",
        "REVOKED",
        "REJECTED",
        "PARTIAL",
        "DETAINED",
        "REPLACED",
        "REPLACEMENT_AUTHORISED",
      ],
    }).notNull(),
    substatus: text("substatus"),
    reasonForStatusChange: text("reason_for_state_change"),
    action: text("action", {
      enum: [
        "RAISE",
        "AMEND",
        "REPLACED",

        "STATUS_CHANGE",
        "RESTORE",
        // SSNS_CLEARED / SSNS_ENTERED / SSNS_UPDATED
        // RESTORE
        // PRINT_FINAL
        // CHARGE_UPDATED
        // REQUEST_REPLACEMENT
        // TRACES_VALIDATION_STATE_CHANGE
        // TRANSFERRED_TO(_PREMISES)
        // AUTO_APPROVAL,
        // TRANSFER_TO_PREMISES,
      ],
    }).notNull(), // this can be extended
    reasonCategories: jsonb("reason_categories"), // Array of reasons selected by user
    overseasAuthorityInvolved: boolean("overseas_authority_involved"), // Nullable boolean flag
    replacesId: varchar("replaces_id", {
      length: id_length,
    }).references(() => exportRequest.id, { onDelete: "set null" }),
    templateVersionId: varchar("template_version_id", {
      length: id_length,
    }).references(() => templateRevision.id, { onDelete: "cascade" }),
    nzxmlURI: text("nzxml_uri"), // URI from NZXML system, if applicable
    snapshotURI: text("snapshot_uri"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by", { length: email_length }), // ID for user
    updatedByProfileName: varchar("updated_by_profile", { length: 20 }), // B2G Profile Name used by user
    updatedBySystem: varchar("updated_by_system", { length: id_length }), // ID for user
  },
)

export const supportingDocumentHistory = coreExportSchema.table(
  "supporting_document_history",
  {
    id: ulidId(), // format should be sdh-<ulid>
    supportingDocumentId: varchar("supporting_document_id", {
      length: id_length,
    }).references(() => supportingDocument.id, { onDelete: "cascade" }),
    currentDocumentVersion: varchar("current_document_version_id"),
    status: text("status", {
      enum: ["ACTIVE", "REPLACED", "CANCELLED"],
    }),
    documentType: text("document_type", {
      enum: ["FIC", "PAC", "IP", "ADH", "ADM", "OTHER"],
    }),
    primaryOperator: varchar("primary_operator", {
      length: id_length,
    }).references(() => partyRegistration.id, { onDelete: "cascade" }),
    intendedOperators: jsonb("intended_operators"), // array of registration ids
    originCountry: text("origin_country"),
    productDescription: text("product_description"),
    identificationMarks: jsonb("identification_marks"), // array of marks
    foreignManufacturePremisesIds: jsonb("foreign_manufacture_premises_ids"), // array of premises ids
    nzManufacturePremiseId: varchar("nz_manufacture_premises_id", {
      length: id_length,
    }).references(() => partyRegistration.id, { onDelete: "cascade" }),
    nzManufacturePremisesName: text("nz_manufacture_premises_name"),
    foreignManufacturePremisesNames: text("foreign_manufacture_premises_names"),
    productionDates: jsonb("production_dates"), // [{ from: date, to: date }] GraphQl [CoreDateRange]
    fileId: varchar("supporting_document_file_id", { length: id_length })
      .notNull()
      .references(() => supportingDocumentFile.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: email_length }), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by", { length: email_length }), // ID for user
  },
)

export const exportRequestOutputEvent = coreExportSchema.table(
  "export_request_output_event",
  {
    id: ulidId(), // crp-<ulid>
    exportRequestOutputId: varchar("export_request_output_id"), // certificates that get generated as a part of request approval
    eventType: text("event_type", {
      enum: ["PRINT"],
    }).default("PRINT"),
    reasonForReprinting: text("reason_for_reprinting"),
    status: text("status", {
      enum: [
        "DRAFT",
        "RAISED",
        "CANCELLED",
        "RESUBMIT",
        "AMENDED",
        "APPROVED",
        "REVOKED",
        "ACKNOWLEDGED",
        "ACCEPTED",
        "REQUEST_REPLACEMENT",
        "PENDING_REPLACEMENT",
        "REJECTED",
        "PARTIAL",
        "DETAINED",
        "REPLACED",
        "REPLACEMENT_AUTHORISED",
      ],
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: id_length }), // ID for user
  },
)

export const xmlGenerationTracking = coreExportSchema.table(
  "xml_generation_tracking",
  {
    requestId: varchar("request_id").notNull(),
    sequenceNumber: integer("sequence_number").notNull(),
    status: text("status").notNull(),
    xmlGenerationStatus: text("xml_generation_status", {
      enum: ["PENDING", "IN_PROGRESS", "COMPLETED", "FAILED"],
    }).notNull(),
    xmlType: text("xml_type", {
      enum: ["OUTPUT", "SUBMISSION", "SUBMISSION_PROCESS_HISTORY"],
    }).notNull(),
    snapshotPath: varchar("snapshot_path"),
    stateMachineArn: text("state_machine_arn"), //currently not being used added this column for future use
    startedAt: timestamp("started_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    primaryKey({
      columns: [table.requestId, table.sequenceNumber, table.xmlType],
    }),
  ],
)
